import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Swords, Loader2 } from "lucide-react";

interface PerkEntry {
  icon: string | null;
  description: string;
}

interface Perk {
  level: number;
  active: boolean;
  icon: string | null;
  perk_icon?: string | null;
  border_icon?: string | null;
  description: string;
  entries?: PerkEntry[];
}

interface Row {
  item_name: string;
  perks: Perk[];
  level_count: number;
  source_url: string | null;
}

interface Props {
  itemName: string;
}

const WeaponProficiencyTable = ({ itemName }: Props) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Row | null>(null);

  const load = async () => {
    setLoading(true);
    const { data: row } = await (supabase as any)
      .from("tibia_weapon_proficiencies")
      .select("*")
      .eq("item_name", itemName)
      .maybeSingle();
    setData(row || null);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemName]);

  const perks = data?.perks || [];

  // Hide entirely for non-weapons (no record) once loaded
  if (!loading && !data) return null;

  return (
    <div className="mt-3">
      <div className="flex items-center gap-1.5 font-pixel text-[7px] tracking-wider text-muted-foreground mb-1 w-full">
        <Swords className="w-3 h-3 text-primary" />
        <span>PROFICIÊNCIA DA ARMA</span>
      </div>

      <div className="tibia-border-inner bg-secondary/20 rounded-sm p-2">
        {loading && (
          <div className="flex justify-center py-3">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
          </div>
        )}

        {!loading && perks.length === 0 && (
          <div className="text-center py-2">
            <p className="text-[10px] text-muted-foreground">
              Esta arma ainda não possui tabela de proficiência registrada.
            </p>
          </div>
        )}

        {!loading && perks.length > 0 && (
          <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${perks.length}, minmax(0, 1fr))` }}>
            {perks.map((perk) => {
              const fallbackEntry: PerkEntry = {
                icon: perk.perk_icon || perk.icon,
                description: perk.description || (perk.active ? "Ativo" : "Inativo"),
              };
              const rawEntries = perk.entries && perk.entries.length > 0 ? perk.entries : [fallbackEntry];

              // Group: entries without description become an overlay on the next entry with description
              type GroupedEntry = PerkEntry & { overlay?: PerkEntry | null };
              const grouped: GroupedEntry[] = [];
              let pendingOverlay: PerkEntry | null = null;
              for (const entry of rawEntries) {
                const hasDesc = !!(entry.description && entry.description.trim());
                if (!hasDesc && entry.icon) {
                  pendingOverlay = entry;
                } else {
                  grouped.push({ ...entry, overlay: pendingOverlay });
                  pendingOverlay = null;
                }
              }
              // If overlay left over with no host, render it standalone
              if (pendingOverlay) {
                grouped.push({ ...pendingOverlay, overlay: null });
              }

              return (
                <div
                  key={perk.level}
                  className={`flex flex-col items-center text-center rounded-sm p-1.5 border ${
                    perk.active
                      ? "border-primary/60 bg-primary/10"
                      : "border-muted/40 bg-muted/10 opacity-60"
                  }`}
                >
                  <div className="font-pixel text-[8px] text-primary mb-1">{perk.level}</div>
                  <div className="flex flex-col gap-2 w-full">
                    {grouped.map((entry, idx) => (
                      <div key={idx} className="flex flex-col items-center">
                        <div className="relative w-10 h-10 flex items-center justify-center mb-0.5">
                          {perk.border_icon && (
                            <img
                              src={perk.border_icon}
                              alt=""
                              className="absolute inset-0 w-full h-full object-contain image-rendering-pixelated"
                              loading="lazy"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                            />
                          )}
                          {entry.icon && (
                            <img
                              src={entry.icon}
                              alt={entry.description || `Perk ${idx + 1}`}
                              className="relative w-7 h-7 object-contain image-rendering-pixelated"
                              loading="lazy"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                            />
                          )}
                          {entry.overlay?.icon && (
                            <img
                              src={entry.overlay.icon}
                              alt=""
                              className="absolute -top-1 -right-1 w-4 h-4 object-contain image-rendering-pixelated z-10 drop-shadow"
                              loading="lazy"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                            />
                          )}
                        </div>
                        <p className="text-[9px] text-foreground leading-tight">
                          {entry.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );

            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WeaponProficiencyTable;
